// Sentry initialisation — only activates when REACT_APP_SENTRY_DSN is set
// (production-safe stub mode otherwise; no events, zero runtime overhead).
//
// To enable: drop your Sentry React-project DSN into frontend/.env:
//   REACT_APP_SENTRY_DSN=https://xxxxx@oXXXXX.ingest.sentry.io/XXXXX
// and rebuild. The init module re-exports a no-op ErrorBoundary fallback
// so the rest of the app code can stay identical whether Sentry is on or off.
import * as Sentry from "@sentry/react";

const dsn = (process.env.REACT_APP_SENTRY_DSN || "").trim();

export const sentryEnabled = Boolean(dsn);

if (sentryEnabled) {
  try {
    Sentry.init({
      dsn,
      environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "production",
      release: "campcompass-web@" + (process.env.REACT_APP_VERSION || "1.0.0"),
      sampleRate: 1.0,
      tracesSampleRate: 0.1,
      sendDefaultPii: false,
      ignoreErrors: [
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications.",
        /Non-Error exception captured/,
        /chrome-extension:/,
        /safari-extension:/,
        // iter125 — In-app browser noise (Facebook / Instagram / TikTok / X
        // inject their own analytics scripts into the WebView; those scripts
        // routinely throw when the user navigates away. None of these errors
        // originate from our code — they're noise we get blamed for because
        // Sentry's auto-instrumentation catches every addEventListener.
        /Java object is gone/i,
        /enableDidUserTypeOnKeyboardLogging/i,
        /sendBeforeUnloadMessage/i,
        /navigation_performance_logger/i,
        /iabjs:\/\//,                  // Facebook in-app browser script
        /InvalidStateError.*unsupported/i,
        /Object Not Found Matching Id/, // FB in-app browser asset gone
        // Network-flakiness noise — user lost signal mid-fetch, not a bug
        /Failed to fetch/,
        /Load failed/,
        /NetworkError when attempting/i,
        /The Internet connection appears to be offline/i,
        /AbortError/,
        // iter214 — Browser-translation extensions mutate the DOM under
        // React's feet, producing NotFoundError on removeChild. Not our
        // bug — nothing we can do beyond `<meta name="google" content="notranslate">`
        // which we already ship in index.html. Filter the residual noise.
        /Failed to execute 'removeChild' on 'Node'/i,
        /The node to be removed is not a child of this node/i,
        /Failed to execute 'insertBefore' on 'Node'/i,
        // iter214 — SW registration in stubbed WebViews sometimes yields
        // a registration object missing addEventListener. Guarded in
        // index.js; suppress any leftover reports.
        /Cannot read properties of undefined \(reading 'addEventListener'\)/i,
        /undefined is not an object \(evaluating '.*\.addEventListener'\)/i,
      ],
      // Drop events from URL schemes that aren't ours (in-app browsers
      // sometimes attribute errors to their own bridge URLs).
      denyUrls: [
        /^iabjs:\/\//,                  // Facebook in-app browser
        /^chrome-extension:\/\//,
        /^safari-extension:\/\//,
        /^moz-extension:\/\//,
      ],
      beforeSend(event) {
        if (process.env.NODE_ENV === "test") return null;
        // Belt-and-braces filter for in-app browser noise that slips past
        // `ignoreErrors` / `denyUrls`. Inspect the stack frames; if ANY
        // frame originates from a known FB/IG/TikTok in-app bridge script,
        // drop the whole event. Same for the well-known message strings.
        try {
          const values = event?.exception?.values || [];
          for (const v of values) {
            const msg = (v && v.value) || "";
            if (/Java object is gone|enableDidUserTypeOnKeyboardLogging|sendBeforeUnloadMessage|navigation_performance_logger|Object Not Found Matching Id/i.test(msg)) {
              return null;
            }
            const frames = (v?.stacktrace?.frames) || [];
            for (const f of frames) {
              const fn = f?.filename || "";
              if (/^iabjs:\/\//i.test(fn)) return null;
              if (/navigation_performance_logger/i.test(fn)) return null;
              if (/\b(chrome|safari|moz)-extension:\/\//i.test(fn)) return null;
            }
          }
        } catch (_) { /* never let filtering crash the SDK */ }

        // Redact obviously sensitive fields if any get attached.
        const data = event?.request?.data;
        if (data && typeof data === "object") {
          ["password", "token", "picture", "secret"].forEach((k) => {
            if (k in data) data[k] = "[REDACTED]";
          });
        }
        return event;
      },
    });
    // Pin a coarse user tag for filtering — only env, no PII.
    Sentry.setTag("app", "campcompass-web");
  } catch (e) {
    // Never let Sentry crash the app on init.
    // eslint-disable-next-line no-console
    console.warn("Sentry init failed:", e);
  }
}

export { Sentry };
